import {
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import { EmailTemplateTypes } from 'shared';

import { useEmailTemplates } from '../../hooks/useEmailTemplates';
import { useGetSelectedOrganisation } from '../../hooks/useGetSelectedOrganisation';
import { classNames } from '../../../common/lib/classNames';
import {
  templateGroupDots,
  templateGroupKey,
  templateGroupLabels,
  templateGroupOrder,
  templateGroupSlugs,
} from '../Settings/EmailTemplates/constants';

// Nested sidebar content for the email templates page: search plus "All
// templates" and one entry (colour dot + count) per template type present in
// the org. Selecting one filters the list via its path, eg
// /settings/email-templates/late-fee. Search filters via the ?q= param, read
// by the list page.
export function EmailTemplateTypesNav() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: emailTemplates } = useEmailTemplates() as any;
  const currentOrg = useGetSelectedOrganisation();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [ searchParams, setSearchParams ] = useSearchParams();

  const basePath = `/${currentOrg?.id}/settings/email-templates`;

  // /:orgId/settings/email-templates[/:typeSlug]
  const activeSlug = pathname.split(`/`).filter(Boolean)[3] ?? null;

  // Match the page's list: legacy late fee templates are hidden there too
  const visibleTemplates = emailTemplates?.filter(t => t.type !== EmailTemplateTypes.LEGACY_LATE_FEE);

  const groupCounts: Record<string, number> = {};

  for (const t of visibleTemplates ?? []) {
    const key = templateGroupKey(t.type);

    groupCounts[key] = (groupCounts[key] ?? 0) + 1;
  }

  const presentGroups = templateGroupOrder.filter(groupKey => groupCounts[groupKey]);

  function onSearchChange(value: string) {
    setSearchParams(value ? { q: value } : {}, { replace: true });
  }

  // Keep the search query when switching type filters
  function goTo(path: string) {
    navigate({ pathname: path, search });
  }

  function linkClasses(active: boolean) {
    return classNames(
      `flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm`,
      active
        ? `bg-gray-100 font-medium text-gray-900`
        : `text-gray-600 hover:bg-gray-100 hover:text-gray-900`,
    );
  }

  return (
    <div className={ `px-2` }>
      <div className={ `relative mb-3 px-1` }>
        <MagnifyingGlassIcon className={ `pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400` } />
        <input
          type={ `search` }
          value={ searchParams.get(`q`) ?? `` }
          onChange={ e => onSearchChange(e.target.value) }
          placeholder={ `Search templates…` }
          className={ `w-full rounded-md border border-gray-300 bg-white py-1.5 pl-8 pr-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-paidniceGreen-500 focus:outline-hidden focus:ring-1 focus:ring-paidniceGreen-500` }
        />
      </div>
      <p className={ `px-2 text-xs font-semibold uppercase tracking-wider text-gray-400` }>
        { `Template Types` }
      </p>
      <div className={ `mt-1 space-y-1` }>
        <button
          type={ `button` }
          onClick={ () => goTo(basePath) }
          className={ linkClasses(!activeSlug) }
        >
          <span>{ `All templates` }</span>
          <span className={ `text-xs font-semibold text-gray-400` }>{ visibleTemplates?.length ?? `` }</span>
        </button>
        { presentGroups.map(groupKey => (
          <button
            key={ groupKey }
            type={ `button` }
            onClick={ () => goTo(`${basePath}/${templateGroupSlugs[groupKey]}`) }
            className={ linkClasses(activeSlug === templateGroupSlugs[groupKey]) }
          >
            <span className={ `flex min-w-0 items-center gap-2` }>
              <span className={ classNames(`h-2 w-2 flex-none rounded-full`, templateGroupDots[groupKey]) } />
              <span className={ `truncate` }>{ templateGroupLabels[groupKey] }</span>
            </span>
            <span className={ `text-xs font-semibold text-gray-400` }>{ groupCounts[groupKey] }</span>
          </button>
        )) }
      </div>
    </div>
  );
}
